import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, Plus, X, Shield, Activity, Thermometer, Edit, Check, Calendar, Stethoscope } from 'lucide-react';

interface HealthSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const HealthSection = ({ data, onChange }: HealthSectionProps) => {
  const [editingVaccination, setEditingVaccination] = useState<number | null>(null);
  const [editingAllergy, setEditingAllergy] = useState<number | null>(null);
  const [editingCheckup, setEditingCheckup] = useState<number | null>(null);

  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const addVaccination = () => {
    const vaccinations = data.vaccinations || [];
    const newIndex = vaccinations.length;
    vaccinations.push({
      vaccine: '',
      manufacturer: '',
      admin_date: '',
      valid_to: '',
      next_due: '',
      administered_by: '',
      batch_number: '',
      clinic_location: '',
      notes: ''
    });
    handleChange('vaccinations', vaccinations);
    setEditingVaccination(newIndex);
  };

  const addCheckup = () => {
    const checkups = data.checkups || [];
    const newIndex = checkups.length;
    checkups.push({
      date: '',
      type: '',
      veterinarian: '',
      clinic: '',
      weight: '',
      temperature: '',
      notes: '',
      next_appointment: ''
    });
    handleChange('checkups', checkups);
    setEditingCheckup(newIndex);
  };

  const updateVaccination = (index: number, field: string, value: string) => {
    const vaccinations = [...(data.vaccinations || [])];
    vaccinations[index] = { ...vaccinations[index], [field]: value };
    handleChange('vaccinations', vaccinations);
  };

  const updateCheckup = (index: number, field: string, value: string) => {
    const checkups = [...(data.checkups || [])];
    checkups[index] = { ...checkups[index], [field]: value };
    handleChange('checkups', checkups);
  };

  const removeVaccination = (index: number) => {
    const vaccinations = data.vaccinations || [];
    vaccinations.splice(index, 1);
    handleChange('vaccinations', vaccinations);
    setEditingVaccination(null);
  };

  const removeCheckup = (index: number) => {
    const checkups = data.checkups || [];
    checkups.splice(index, 1);
    handleChange('checkups', checkups);
    setEditingCheckup(null);
  };

  const saveVaccination = (index: number) => {
    setEditingVaccination(null);
  };

  const saveCheckup = (index: number) => {
    setEditingCheckup(null);
  };

  const addAllergy = () => {
    const allergies = data.allergies || [];
    const newIndex = allergies.length;
    allergies.push({
      allergen: '',
      allergen_type: 'food',
      reaction: '',
      severity: 'mild',
      treatment: '',
      notes: '',
      first_occurrence: '',
      last_occurrence: ''
    });
    handleChange('allergies', allergies);
    setEditingAllergy(newIndex);
  };

  const updateAllergy = (index: number, field: string, value: string) => {
    const allergies = [...(data.allergies || [])];
    allergies[index] = { ...allergies[index], [field]: value };
    handleChange('allergies', allergies);
  };

  const removeAllergy = (index: number) => {
    const allergies = data.allergies || [];
    allergies.splice(index, 1);
    handleChange('allergies', allergies);
    setEditingAllergy(null);
  };

  const saveAllergy = (index: number) => {
    setEditingAllergy(null);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Informazioni Generali */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Informazioni Generali sulla Salute
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="microchip">Numero Microchip</Label>
              <Input
                id="microchip"
                value={data.microchip || ''}
                onChange={(e) => handleChange('microchip', e.target.value)}
                placeholder="15 cifre"
              />
            </div>
            <div>
              <Label htmlFor="blood_type">Gruppo Sanguigno</Label>
              <Select value={data.blood_type || ''} onValueChange={(value) => handleChange('blood_type', value)}>
                <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                  <SelectValue placeholder="Seleziona gruppo" />
                </SelectTrigger>
                <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                  <SelectItem value="DEA 1.1+">DEA 1.1+ (Cane)</SelectItem>
                  <SelectItem value="DEA 1.1-">DEA 1.1- (Cane)</SelectItem>
                  <SelectItem value="A">A (Gatto)</SelectItem>
                  <SelectItem value="B">B (Gatto)</SelectItem>
                  <SelectItem value="AB">AB (Gatto)</SelectItem>
                  <SelectItem value="unknown">Non determinato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="medical_conditions">Condizioni Mediche</Label>
            <Textarea
              id="medical_conditions"
              value={data.medical_conditions || ''}
              onChange={(e) => handleChange('medical_conditions', e.target.value)}
              placeholder="Condizioni mediche croniche, disabilità, etc."
            />
          </div>
        </CardContent>
      </Card>

      {/* Vaccinazioni */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Vaccinazioni
            </div>
            <Button onClick={addVaccination} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Vaccinazione
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.vaccinations && data.vaccinations.length > 0 ? (
            <div className="space-y-4">
              {data.vaccinations.map((vaccination: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  {editingVaccination === index ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Vaccino *</Label>
                          <Input
                            value={vaccination.vaccine || ''}
                            onChange={(e) => updateVaccination(index, 'vaccine', e.target.value)}
                            placeholder="Nome del vaccino"
                          />
                        </div>
                        <div>
                          <Label>Produttore</Label>
                          <Input
                            value={vaccination.manufacturer || ''}
                            onChange={(e) => updateVaccination(index, 'manufacturer', e.target.value)}
                            placeholder="Casa farmaceutica"
                          />
                        </div>
                        <div>
                          <Label>Data Somministrazione *</Label>
                          <Input
                            type="date"
                            value={vaccination.admin_date || ''}
                            onChange={(e) => updateVaccination(index, 'admin_date', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Valido fino a</Label>
                          <Input
                            type="date"
                            value={vaccination.valid_to || ''}
                            onChange={(e) => updateVaccination(index, 'valid_to', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Prossimo Richiamo</Label>
                          <Input
                            type="date"
                            value={vaccination.next_due || ''}
                            onChange={(e) => updateVaccination(index, 'next_due', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Veterinario</Label>
                          <Input
                            value={vaccination.administered_by || ''}
                            onChange={(e) => updateVaccination(index, 'administered_by', e.target.value)}
                            placeholder="Nome del veterinario"
                          />
                        </div>
                        <div>
                          <Label>Numero di Lotto</Label>
                          <Input
                            value={vaccination.batch_number || ''}
                            onChange={(e) => updateVaccination(index, 'batch_number', e.target.value)}
                            placeholder="Lotto del vaccino"
                          />
                        </div>
                        <div>
                          <Label>Clinica</Label>
                          <Input
                            value={vaccination.clinic_location || ''}
                            onChange={(e) => updateVaccination(index, 'clinic_location', e.target.value)}
                            placeholder="Nome della clinica"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Note</Label>
                        <Textarea
                          value={vaccination.notes || ''}
                          onChange={(e) => updateVaccination(index, 'notes', e.target.value)}
                          placeholder="Note aggiuntive"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveVaccination(index)} size="sm">
                          <Check className="w-4 h-4 mr-2" />
                          Salva
                        </Button>
                        <Button variant="outline" onClick={() => removeVaccination(index)} size="sm">
                          <X className="w-4 h-4 mr-2" />
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{vaccination.vaccine || 'Vaccinazione'}</h4>
                          {vaccination.admin_date && (
                            <Badge variant="secondary">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(vaccination.admin_date).toLocaleDateString('it-IT')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {vaccination.manufacturer && <p>Produttore: {vaccination.manufacturer}</p>}
                          {vaccination.administered_by && <p>Veterinario: {vaccination.administered_by}</p>}
                          {vaccination.next_due && (
                            <p className="text-orange-600">
                              Prossimo richiamo: {new Date(vaccination.next_due).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingVaccination(index)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeVaccination(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessuna vaccinazione registrata. Clicca "Aggiungi Vaccinazione" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Controlli Medici */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-green-500" />
              Controlli Medici
            </div>
            <Button onClick={addCheckup} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Controllo
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.checkups && data.checkups.length > 0 ? (
            <div className="space-y-4">
              {data.checkups.map((checkup: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  {editingCheckup === index ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Data Controllo *</Label>
                          <Input
                            type="date"
                            value={checkup.date || ''}
                            onChange={(e) => updateCheckup(index, 'date', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Tipo di Controllo *</Label>
                          <Select value={checkup.type || ''} onValueChange={(value) => updateCheckup(index, 'type', value)}>
                            <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                              <SelectValue placeholder="Seleziona tipo" />
                            </SelectTrigger>
                            <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                              <SelectItem value="routine">Controllo di Routine</SelectItem>
                              <SelectItem value="specialist">Visita Specialistica</SelectItem>
                              <SelectItem value="emergency">Visita d'Emergenza</SelectItem>
                              <SelectItem value="surgery">Intervento Chirurgico</SelectItem>
                              <SelectItem value="dental">Controllo Dentale</SelectItem>
                              <SelectItem value="vaccination">Controllo Vaccinale</SelectItem>
                              <SelectItem value="dermatology">Dermatologia</SelectItem>
                              <SelectItem value="ophthalmology">Oftalmologia</SelectItem>
                              <SelectItem value="cardiology">Cardiologia</SelectItem>
                              <SelectItem value="orthopedics">Ortopedia</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Veterinario</Label>
                          <Input
                            value={checkup.veterinarian || ''}
                            onChange={(e) => updateCheckup(index, 'veterinarian', e.target.value)}
                            placeholder="Nome del veterinario"
                          />
                        </div>
                        <div>
                          <Label>Clinica</Label>
                          <Input
                            value={checkup.clinic || ''}
                            onChange={(e) => updateCheckup(index, 'clinic', e.target.value)}
                            placeholder="Nome della clinica"
                          />
                        </div>
                         <div>
                           <Label>Peso Registrato (kg) *</Label>
                           <Input
                             type="number"
                             step="0.1"
                             value={checkup.weight || ''}
                             onChange={(e) => updateCheckup(index, 'weight', e.target.value)}
                             placeholder="Peso registrato durante il controllo"
                           />
                         </div>
                        <div>
                          <Label>Temperatura (°C)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={checkup.temperature || ''}
                            onChange={(e) => updateCheckup(index, 'temperature', e.target.value)}
                            placeholder="Temperatura corporea"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Prossimo Appuntamento</Label>
                          <Input
                            type="date"
                            value={checkup.next_appointment || ''}
                            onChange={(e) => updateCheckup(index, 'next_appointment', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Note e Risultati</Label>
                        <Textarea
                          value={checkup.notes || ''}
                          onChange={(e) => updateCheckup(index, 'notes', e.target.value)}
                          placeholder="Risultati dell'esame, prescrizioni, note del veterinario..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveCheckup(index)} size="sm">
                          <Check className="w-4 h-4 mr-2" />
                          Salva
                        </Button>
                        <Button variant="outline" onClick={() => removeCheckup(index)} size="sm">
                          <X className="w-4 h-4 mr-2" />
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{checkup.type || 'Controllo medico'}</h4>
                          {checkup.date && (
                            <Badge variant="secondary">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(checkup.date).toLocaleDateString('it-IT')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {checkup.veterinarian && <p>Veterinario: {checkup.veterinarian}</p>}
                          {checkup.clinic && <p>Clinica: {checkup.clinic}</p>}
                          {checkup.weight && <p>Peso: {checkup.weight} kg</p>}
                          {checkup.next_appointment && (
                            <p className="text-blue-600">
                              Prossimo appuntamento: {new Date(checkup.next_appointment).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingCheckup(index)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeCheckup(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessun controllo medico registrato. Clicca "Aggiungi Controllo" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Allergie */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-500" />
              Allergie e Reazioni
            </div>
            <Button onClick={addAllergy} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Allergia
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.allergies && data.allergies.length > 0 ? (
            <div className="space-y-4">
              {data.allergies.map((allergy: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  {editingAllergy === index ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Allergene *</Label>
                          <Input
                            value={allergy.allergen || ''}
                            onChange={(e) => updateAllergy(index, 'allergen', e.target.value)}
                            placeholder="Sostanza che causa l'allergia"
                          />
                        </div>
                        <div>
                          <Label>Tipo Allergene *</Label>
                          <Select value={allergy.allergen_type || ''} onValueChange={(value) => updateAllergy(index, 'allergen_type', value)}>
                            <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                              <SelectValue placeholder="Seleziona tipo" />
                            </SelectTrigger>
                            <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                              <SelectItem value="food">Alimentare</SelectItem>
                              <SelectItem value="medication">Farmaci/Medicine</SelectItem>
                              <SelectItem value="environmental">Ambientale (polline, polvere)</SelectItem>
                              <SelectItem value="contact">Contatto (materiali, tessuti)</SelectItem>
                              <SelectItem value="flea">Pulci/Parassiti</SelectItem>
                              <SelectItem value="seasonal">Stagionale</SelectItem>
                              <SelectItem value="chemical">Chimica (detergenti, shampoo)</SelectItem>
                              <SelectItem value="other">Altro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Gravità *</Label>
                          <Select value={allergy.severity || ''} onValueChange={(value) => updateAllergy(index, 'severity', value)}>
                            <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                              <SelectValue placeholder="Seleziona gravità" />
                            </SelectTrigger>
                            <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                              <SelectItem value="mild">Lieve</SelectItem>
                              <SelectItem value="moderate">Moderata</SelectItem>
                              <SelectItem value="severe">Grave</SelectItem>
                              <SelectItem value="life_threatening">Pericolosa per la vita</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Prima Comparsa</Label>
                          <Input
                            type="date"
                            value={allergy.first_occurrence || ''}
                            onChange={(e) => updateAllergy(index, 'first_occurrence', e.target.value)}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Reazione</Label>
                          <Textarea
                            value={allergy.reaction || ''}
                            onChange={(e) => updateAllergy(index, 'reaction', e.target.value)}
                            placeholder="Descrivi i sintomi e la reazione (prurito, orticaria, difficoltà respiratorie, vomito...)"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Trattamento</Label>
                          <Textarea
                            value={allergy.treatment || ''}
                            onChange={(e) => updateAllergy(index, 'treatment', e.target.value)}
                            placeholder="Trattamento e farmaci utilizzati (antistaminici, cortisonici, adrenalina...)"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Note</Label>
                          <Textarea
                            value={allergy.notes || ''}
                            onChange={(e) => updateAllergy(index, 'notes', e.target.value)}
                            placeholder="Note aggiuntive, precauzioni, trigger specifici..."
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveAllergy(index)} size="sm">
                          <Check className="w-4 h-4 mr-2" />
                          Salva
                        </Button>
                        <Button variant="outline" onClick={() => removeAllergy(index)} size="sm">
                          <X className="w-4 h-4 mr-2" />
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{allergy.allergen || 'Allergia'}</h4>
                            <Badge variant={allergy.severity === 'life_threatening' ? 'destructive' : allergy.severity === 'severe' ? 'destructive' : allergy.severity === 'moderate' ? 'default' : 'secondary'}>
                              {allergy.severity === 'mild' ? 'Lieve' : 
                               allergy.severity === 'moderate' ? 'Moderata' : 
                               allergy.severity === 'severe' ? 'Grave' :
                               allergy.severity === 'life_threatening' ? 'Pericolosa' : 'Non specificata'}
                            </Badge>
                            {allergy.allergen_type && (
                              <Badge variant="outline">
                                {allergy.allergen_type === 'food' ? 'Alimentare' :
                                 allergy.allergen_type === 'medication' ? 'Farmaci' :
                                 allergy.allergen_type === 'environmental' ? 'Ambientale' :
                                 allergy.allergen_type === 'contact' ? 'Contatto' :
                                 allergy.allergen_type === 'flea' ? 'Parassiti' :
                                 allergy.allergen_type === 'seasonal' ? 'Stagionale' :
                                 allergy.allergen_type === 'chemical' ? 'Chimica' : 'Altro'}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {allergy.reaction && <p><strong>Reazione:</strong> {allergy.reaction}</p>}
                            {allergy.treatment && <p><strong>Trattamento:</strong> {allergy.treatment}</p>}
                            {allergy.first_occurrence && (
                              <p><strong>Prima comparsa:</strong> {new Date(allergy.first_occurrence).toLocaleDateString('it-IT')}</p>
                            )}
                          </div>
                        </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingAllergy(index)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeAllergy(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessuna allergia registrata. Clicca "Aggiungi Allergia" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Note Mediche Generali */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-purple-500" />
            Note Mediche Generali
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.medical_notes || ''}
            onChange={(e) => handleChange('medical_notes', e.target.value)}
            placeholder="Note generali sulla salute, osservazioni particolari, promemoria medici..."
            rows={6}
          />
        </CardContent>
      </Card>
    </div>
  );
};